// Orders management hooks
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { supabase } from '@/lib/supabase'
import { queryKeys } from '@/lib/query-client'
import { isAssistantLoggedIn } from '@/lib/permissions'
import { handleAuthorizationError } from '@/lib/auth-error-handler'
import type {  Order } from '@/types/entities'
import type { OrderStatus } from '@/types/api.types'

// Track last manual refetch per restaurant to prevent duplicate fetches
const lastManualRefetchByRestaurant = new Map<string, number>()

// Track last auto-refetch timestamp globally (shared across all useOrders instances)
// This prevents multiple component instances from triggering simultaneous refetches
const globalRefetchState = {
  lastAutoRefetchAt: 0,
  lastResumeRefetchAt: 0,
}

/**
 * Fetch all orders for a specific restaurant
 */
export function useOrders(restaurantId: string | null) {
  const queryClient = useQueryClient()

  const query = useQuery({
    queryKey: queryKeys.orders.list({ restaurantId: restaurantId || '' }),
    structuralSharing: false, // Disable structural sharing to avoid circular reference issues with parent/sub-order structure
    staleTime: 2 * 60 * 1000, // 2 minutes (realtime handles updates)
    gcTime: 10 * 60 * 1000, // 10 minutes cache
    // IMPORTANT: Disable React Query's built-in refetch on focus - we handle it manually
    // with proper throttling in the realtime subscription effect below.
    // This prevents duplicate refetches when focus events fire.
    refetchOnWindowFocus: false,
    refetchOnMount: false, // Don't refetch if data is fresh
    queryFn: async (): Promise<Order[]> => {
      if (!restaurantId) {
        return []
      }

      // Use ensureQueryData to deduplicate with useTables hook - prevents duplicate fetches
      // If useTables is already fetching, this will wait for that query instead of making a new one
      const tablesPromise = queryClient.ensureQueryData({
        queryKey: queryKeys.tables.list(restaurantId),
        queryFn: async () => {
          const { data, error } = await supabase.rpc('get_restaurant_tables', { p_restaurant_id: restaurantId })
          if (error) throw error
          return data
        },
        staleTime: 30 * 1000, // Match useTables staleTime
      })

      // Check cache for menu items
      const cachedMenuItems = queryClient.getQueryData(queryKeys.menuItems.raw(restaurantId))

      // Fetch what's needed - use ensureQueryData for tables to deduplicate
      const [ordersResult, orderItemsResult, tablesData, menuItemsResult] = await Promise.all([
        supabase.rpc('get_restaurant_orders', { p_restaurant_id: restaurantId }),
        (supabase.rpc as any)('get_restaurant_order_items', { p_restaurant_id: restaurantId }),
        tablesPromise,
        cachedMenuItems
          ? Promise.resolve({ data: cachedMenuItems, error: null })
          : supabase.rpc('get_restaurant_menu_items', { p_restaurant_id: restaurantId }),
      ])

      const ordersData = ordersResult.data
      const allOrderItemsData = orderItemsResult.data || []
      // tablesData is already the raw data from ensureQueryData (not wrapped in { data, error })
      const menuItemsData = menuItemsResult.data

      if (ordersResult.error) {
        throw ordersResult.error
      }

      // Cache menu items data (tables are managed by useTables hook to avoid data format conflicts)
      if (menuItemsData && !cachedMenuItems) {
        queryClient.setQueryData(queryKeys.menuItems.raw(restaurantId), menuItemsData, {
          updatedAt: Date.now(),
        })
      }

      if (!ordersData || ordersData.length === 0) {
        return []
      }

      // Create lookup maps
      const tablesMap = new Map((tablesData as any[])?.map((t: any) => [t.id, t]) || [])
      const menuItemsMap = new Map((menuItemsData as any[])?.map((m: any) => [m.id, m]) || [])
      
      // Group order items by order_id
      const orderItemsMap = new Map<string, any[]>()
      allOrderItemsData.forEach((item: any) => {
        const orderId = item.order_id
        if (!orderItemsMap.has(orderId)) {
          orderItemsMap.set(orderId, [])
        }
        orderItemsMap.get(orderId)!.push(item)
      })

      // Build orders with parent_order_id
      const ordersMap = new Map<string, Order>()
      const subOrdersMap = new Map<string, Order[]>()

      ordersData.forEach((o: any) => {
        const table = tablesMap.get(o.table_id) as any
        const orderItemsData = orderItemsMap.get(o.id) || []

        const order: Order = {
          id: o.id,
          tableId: o.table_id,
          serverId: o.server_id || o.staff_id || '',
          serverUsername: o.server_username || null,
          status: o.status as OrderStatus,
          readyAt: o.ready_at || null,
          completedAt: o.completed_at || null,
          createdAt: o.created_at || '',
          updatedAt: o.updated_at || '',
          parentOrderId: o.parent_order_id || null,
          cancellationRequestedBy: o.cancellation_requested_by || null,
          cancellationConfirmedBy: o.cancellation_confirmed_by || null,
          cancellationConfirmedByName: o.cancellation_confirmed_by_name || null,
          cancellationRequestedAt: o.cancellation_requested_at || null,
          items: orderItemsData.map((item: any) => {
            const menuItem = menuItemsMap.get(item.item_menu_item_id) as any
            const orderItem: any = {
              id: item.item_id,
              orderId: item.order_id,
              menuItemId: item.item_menu_item_id,
              menuItemOptionId: item.item_menu_item_option_id || null,
              quantity: item.item_quantity,
              priceAtOrder: item.item_price_at_order,
              notes: item.item_notes || null,
              createdAt: item.item_created_at || '',
              updatedAt: item.updated_at || '',
            }
            if (menuItem) {
              orderItem.menuItem = {
                id: menuItem.id,
                restaurantId: menuItem.restaurant_id,
                nameAr: menuItem.name_ar,
                nameFr: menuItem.name_fr,
                descriptionAr: menuItem.description_ar || null,
                descriptionFr: menuItem.description_fr || null,
                price: menuItem.price,
                imageUrl: menuItem.image_url || null,
                imagePath: menuItem.image_path || null,
                isAvailable: menuItem.is_available ?? true,
                createdAt: menuItem.created_at || '',
                updatedAt: menuItem.updated_at || '',
              }
            }
            // Add menu item option if present
            if (item.item_menu_item_option_id && item.menu_item_option_name_ar) {
              orderItem.menuItemOption = {
                id: item.item_menu_item_option_id,
                menuItemId: item.item_menu_item_id,
                nameAr: item.menu_item_option_name_ar,
                nameFr: item.menu_item_option_name_fr,
                priceModifier: item.menu_item_option_price_modifier || 0,
                isDefault: false,
                sortOrder: 0,
                createdAt: '',
                updatedAt: '',
              }
            }
            // Add specialty IDs for kitchen filtering
            orderItem.specialtyIds = item.specialty_ids || []
            // Add portions for specialty-based kitchen management
            if (item.portions && Array.isArray(item.portions)) {
              orderItem.portions = item.portions.map((p: any) => ({
                id: p.id,
                orderItemId: item.item_id,
                specialtyId: p.specialty_id,
                status: p.status,
                updatedAt: p.updated_at,
                updatedBy: p.updated_by || null,
                specialtyNameAr: p.specialty_name_ar,
                specialtyNameFr: p.specialty_name_fr,
                specialtyColor: p.specialty_color,
              }))
            } else {
              orderItem.portions = []
            }
            return orderItem
          }),
        }
        if (table) {
          order.table = {
            id: table.id,
            tableNumber: table.table_number ?? table.tableNumber,
            seats: table.seats,
            zoneId: table.zone_id ?? null,
            status: table.status || 'available',
            createdAt: table.created_at ?? table.createdAt ?? '',
            updatedAt: table.updated_at ?? table.updatedAt ?? '',
          }
        }
        
        // Group by parent/sub-order structure
        if (order.parentOrderId) {
          // This is a sub-order
          if (!subOrdersMap.has(order.parentOrderId)) {
            subOrdersMap.set(order.parentOrderId, [])
          }
          subOrdersMap.get(order.parentOrderId)!.push(order)
        } else {
          // This is a parent order
          ordersMap.set(order.id, order)
        }
      })
      
      // Attach sub-orders to parent orders (without circular references)
      subOrdersMap.forEach((subOrders, parentId) => {
        const parentOrder = ordersMap.get(parentId)
        if (parentOrder) {
          // Create a new array to avoid mutation issues
          parentOrder.subOrders = subOrders.map(subOrder => ({
            ...subOrder,
            // Only include parentOrderId, not the full parentOrder object to avoid circular reference
            parentOrderId: subOrder.parentOrderId || parentId
          }))
        }
      })

      // Return parent orders (sub-orders are nested within)
      const finalOrders = Array.from(ordersMap.values())
      return finalOrders
    },
    enabled: !!restaurantId,
  })

  // Track auth state for realtime subscription
  const [isAuthReady, setIsAuthReady] = useState(false)

  // Wait for auth session to be ready before setting up realtime
  useEffect(() => {
    let isMounted = true

    const checkAuthAndSetReady = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession()
        if (isMounted && session) {
          setIsAuthReady(true)
        }
      } catch {
        // Ignore errors, we'll retry via onAuthStateChange
      }
    }

    // Check immediately
    checkAuthAndSetReady()

    // Also listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (isMounted) {
        setIsAuthReady(!!session)
      }
    })

    return () => {
      isMounted = false
      subscription.unsubscribe()
    }
  }, [])

  // Real-time subscription for orders
  useEffect(() => {
    if (!restaurantId || !isAuthReady) {
      return
    }

    // Remove any existing order channels for this restaurant to prevent conflicts
    const existingChannels = supabase.getChannels()
    existingChannels.forEach((ch) => {
      if (ch.topic.includes(`orders-changes-${restaurantId}`)) {
        supabase.removeChannel(ch)
      }
    })

    // Create a stable channel name per restaurant
    const channelName = `orders-changes-${restaurantId}`

    // Debounce timer for invalidation
    let invalidateTimer: NodeJS.Timeout | null = null
    let refetchTimer: NodeJS.Timeout | null = null
    let reconnectTimer: NodeJS.Timeout | null = null
    let watchdogTimer: NodeJS.Timeout | null = null
    let pollTimer: NodeJS.Timeout | null = null
    let isUnmounted = false

    // Track last time we received a realtime event for this restaurant.
    // If this goes stale (common on tablets after long idle), we resubscribe + refetch.
    let lastRealtimeEventAt = Date.now()
    let lastChannelStatus: string | null = null
    let channel: ReturnType<typeof supabase.channel> | null = null
    let activeChannelGeneration = 0
    let lastReconnectAt = 0
    let reconnectInProgress = false

    // Debounced invalidation function - groups multiple rapid-fire events into a single invalidation
    // This marks queries as stale without forcing a refetch - React Query will handle smart refetching
    const handleInvalidate = () => {
      if (!restaurantId) return

      // Skip invalidation if we just did a manual operation (within last 10 seconds)
      // This prevents duplicate fetches when user makes a change (mutation triggers refetch, realtime also fires)
      // Window must be longer than the realtime debounce interval (5s) to prevent late realtime refetches
      const lastRefetch = lastManualRefetchByRestaurant.get(restaurantId) || 0
      const timeSinceLastRefetch = Date.now() - lastRefetch
      if (timeSinceLastRefetch < 10000) {
        return
      }

      // Clear any pending invalidation
      if (invalidateTimer) {
        clearTimeout(invalidateTimer)
      }

      // Schedule invalidation after 100ms of inactivity
      invalidateTimer = setTimeout(() => {
        // IMPORTANT: portions/status updates can generate a burst of events.
        // We mark the query stale but we DO NOT auto-refetch on every invalidate.
        // Instead we do one throttled/batched refetch (see scheduleBatchedRefetch below).
        queryClient.invalidateQueries({
          queryKey: queryKeys.orders.list({ restaurantId }),
          refetchType: 'none',
        })
        invalidateTimer = null
        scheduleBatchedRefetch()
      }, 100)
    }

    // Filter realtime noise: only invalidate if the change affects this restaurant.
    // (order_items / portions tables don't include restaurant_id, so we filter via cached order graph)
    const isRelevantOrderId = (orderId: string | null | undefined) => {
      if (!orderId) return false
      const cached = queryClient.getQueryData<Order[]>(queryKeys.orders.list({ restaurantId })) || []
      for (const parent of cached) {
        if (parent.id === orderId) return true
        if (parent.subOrders?.some((so) => so.id === orderId)) return true
      }
      return false
    }

    const isRelevantOrderItemId = (orderItemId: string | null | undefined) => {
      if (!orderItemId) return false
      const cached = queryClient.getQueryData<Order[]>(queryKeys.orders.list({ restaurantId })) || []
      for (const parent of cached) {
        for (const item of parent.items || []) {
          if ((item as any).id === orderItemId) return true
        }
        for (const so of parent.subOrders || []) {
          for (const item of so.items || []) {
            if ((item as any).id === orderItemId) return true
          }
        }
      }
      return false
    }

    const refetchOrdersNow = () => {
      if (!restaurantId) return
      queryClient.refetchQueries({
        queryKey: queryKeys.orders.list({ restaurantId }),
        type: 'active',
      })
    }

    const scheduleBatchedRefetch = () => {
      if (!restaurantId) return
      if (refetchTimer) {
        clearTimeout(refetchTimer)
      }

      // Batch bursts of realtime events into one refetch.
      // Use a longer interval (5s) to avoid request storms, especially with multiple tabs open.
      // Realtime events provide the notification - we only need periodic refetch to sync.
      refetchTimer = setTimeout(() => {
        if (isUnmounted) return
        const now = Date.now()
        // Enforce minimum 5 second interval between auto-refetches (global across all instances)
        if (now - globalRefetchState.lastAutoRefetchAt < 5000) {
          // Reschedule for later if we're within the throttle window
          refetchTimer = setTimeout(() => {
            if (isUnmounted) return
            const retryNow = Date.now()
            if (retryNow - globalRefetchState.lastAutoRefetchAt >= 5000) {
              globalRefetchState.lastAutoRefetchAt = retryNow
              refetchOrdersNow()
            }
          }, 5000 - (now - globalRefetchState.lastAutoRefetchAt))
          return
        }
        globalRefetchState.lastAutoRefetchAt = now
        refetchOrdersNow()
      }, 500)
    }

    const clearReconnectTimer = () => {
      if (reconnectTimer) {
        clearTimeout(reconnectTimer)
        reconnectTimer = null
      }
    }

    const removeCurrentChannel = () => {
      if (channel) {
        // Bump generation so late status callbacks from the old channel are ignored.
        activeChannelGeneration += 1
        supabase.removeChannel(channel)
        channel = null
      }
    }

    const scheduleReconnect = (reason: string, delayMs: number) => {
      clearReconnectTimer()
      reconnectTimer = setTimeout(() => {
        if (isUnmounted) return
        // Only attempt reconnect when the tab is visible; otherwise wait until resume handlers run.
        if (typeof document !== 'undefined' && document.hidden) return
        // Prevent reconnect loops / thundering herd of reconnect triggers.
        const now = Date.now()
        if (reconnectInProgress) return
        if (now - lastReconnectAt < 5000) return // Increased from 2s to 5s
        reconnectInProgress = true
        lastReconnectAt = now

        removeCurrentChannel()
        channel = createAndSubscribeChannel()
        // On reconnect, refetch to recover missed events (respecting global throttle)
        if (now - globalRefetchState.lastAutoRefetchAt > 5000) {
          globalRefetchState.lastAutoRefetchAt = now
          refetchOrdersNow()
        }
      }, delayMs)
    }

    const createAndSubscribeChannel = () => {
      const myGeneration = activeChannelGeneration
      const ch = supabase
        .channel(channelName)
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'orders',
            filter: `restaurant_id=eq.${restaurantId}`,
          },
          (payload: any) => {
            lastRealtimeEventAt = Date.now()
          // Handle INSERT events (no payload.old) and UPDATE events
          const payloadNew = payload.new as any
          const payloadOld = payload.old as any
          const payloadRestaurantId = payloadNew?.restaurant_id || payloadOld?.restaurant_id

          // Filter by restaurant_id in the callback
          if (!payloadRestaurantId || payloadRestaurantId !== restaurantId) {
            return // Ignore orders from other restaurants
          }

          // Handle INSERT events - new order submitted
          if (payload.eventType === 'INSERT') {
            const isSubOrder = !!payloadNew.parent_order_id
            window.dispatchEvent(new CustomEvent('order-inserted', {
              detail: {
                orderId: payloadNew.id,
                tableId: payloadNew.table_id,
                restaurantId: payloadRestaurantId,
                status: payloadNew.status,
                isSubOrder
              }
            }))
          }

          // Handle UPDATE events - status changes
          if (payload.eventType === 'UPDATE') {
            const oldStatus = payloadOld?.status
            const newStatus = payloadNew?.status
            const isSubOrder = !!payloadNew.parent_order_id

            // Only dispatch if status actually changed
            if (oldStatus && newStatus && oldStatus !== newStatus) {
              // Dispatch generic status change event
              window.dispatchEvent(new CustomEvent('order-status-changed', {
                detail: {
                  orderId: payloadNew.id,
                  tableId: payloadNew.table_id,
                  restaurantId: payloadRestaurantId,
                  oldStatus,
                  newStatus,
                  isSubOrder
                }
              }))

              // Also dispatch specific sub-order-ready event for ServerDashboard compatibility
              if (isSubOrder && oldStatus !== 'ready' && newStatus === 'ready') {
                window.dispatchEvent(new CustomEvent('sub-order-ready', {
                  detail: {
                    orderId: payloadNew.id,
                    tableId: payloadNew.table_id,
                    restaurantId: payloadRestaurantId
                  }
                }))
              }
            }

            // Check if cancellation was requested (for admin and kitchen notifications)
            const oldCancellationRequested = payloadOld?.cancellation_requested_by
            const newCancellationRequested = payloadNew?.cancellation_requested_by

            // If cancellation was just requested, trigger notification event for admin
            if (!oldCancellationRequested && newCancellationRequested) {
              window.dispatchEvent(new CustomEvent('cancellation-requested', {
                detail: {
                  orderId: payloadNew.id,
                  tableId: payloadNew.table_id,
                  restaurantId: payloadRestaurantId,
                  requestedBy: newCancellationRequested
                }
              }))
            }
          }

          // Invalidate queries when database changes occur
          handleInvalidate()
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'order_items',
        },
        (payload: any) => {
          lastRealtimeEventAt = Date.now()
          // Invalidate only if the order belongs to this restaurant
          const payloadNew = payload.new as any
          const payloadOld = payload.old as any
          const orderId = payloadNew?.order_id || payloadOld?.order_id
          if (!isRelevantOrderId(orderId)) return
          handleInvalidate()
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'order_item_portions',
        },
        (payload: any) => {
          lastRealtimeEventAt = Date.now()
          // Filter out portions that don't belong to this restaurant
          const payloadNew = payload.new as any
          const payloadOld = payload.old as any
          const orderItemId = payloadNew?.order_item_id || payloadOld?.order_item_id
          if (!isRelevantOrderItemId(orderItemId)) return

          // Handle portion status changes
          if (payload.eventType === 'UPDATE') {
            const oldStatus = payloadOld?.status
            const newStatus = payloadNew?.status

            // Dispatch event when portion status changes to ready
            // Note: oldStatus might be undefined if REPLICA IDENTITY is not FULL
            if (newStatus && (oldStatus !== newStatus || !oldStatus)) {
              window.dispatchEvent(new CustomEvent('portion-status-changed', {
                detail: {
                  portionId: payloadNew.id,
                  orderItemId: payloadNew.order_item_id,
                  specialtyId: payloadNew.specialty_id,
                  oldStatus: oldStatus || null,
                  newStatus,
                }
              }))
            }
          }

          // Invalidate when order item portions change
          handleInvalidate()
        }
      )
      .subscribe((status) => {
        // Ignore late callbacks from a channel we already replaced/removed.
        if (myGeneration !== activeChannelGeneration) return
        lastChannelStatus = status

        if (status === 'SUBSCRIBED') {
          reconnectInProgress = false
          // Consider the channel healthy; reset staleness clock.
          lastRealtimeEventAt = Date.now()
          return
        }

        // If the channel errors/closes/times out, resubscribe.
        // This is especially important on iOS where sockets can silently die while the tab stays open.
        if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || status === 'CLOSED') {
          scheduleReconnect(`channel:${status}`, 1200)
        }
      })

      return ch
    }

    channel = createAndSubscribeChannel()

    const handleResume = () => {
      if (!restaurantId) return
      if (typeof document !== 'undefined' && document.hidden) return

      // Throttle resume refetch more aggressively (10 seconds) to avoid storms
      // from multiple focus/visibility/pageshow events firing in quick succession.
      // This is global across all useOrders instances to prevent duplicate refetches.
      const now = Date.now()
      if (now - globalRefetchState.lastResumeRefetchAt > 10000) {
        globalRefetchState.lastResumeRefetchAt = now
        // Refetch on resume/focus/online to recover missed events.
        refetchOrdersNow()
      }

      // If the channel isn't subscribed (or has been stale for 5+ minutes), kick it.
      // Increased staleness threshold to reduce unnecessary reconnects.
      const staleMs = Date.now() - lastRealtimeEventAt
      if (lastChannelStatus !== 'SUBSCRIBED' || staleMs > 5 * 60 * 1000) {
        scheduleReconnect('resume:stale', 0)
      }
    }

    // Tablet-friendly: when the page becomes visible again or network returns, refresh + ensure channel is alive.
    window.addEventListener('focus', handleResume)
    window.addEventListener('pageshow', handleResume)
    window.addEventListener('online', handleResume)
    document.addEventListener('visibilitychange', handleResume)

    // Watchdog: if visible + we haven't seen realtime in a while, restart the channel.
    // Check every 60 seconds, trigger reconnect if stale for 5+ minutes.
    // Note: we don't refetch here - the reconnect will trigger a refetch.
    watchdogTimer = setInterval(() => {
      if (typeof document !== 'undefined' && document.hidden) return
      const staleMs = Date.now() - lastRealtimeEventAt
      if (staleMs > 5 * 60 * 1000) {
        scheduleReconnect('watchdog:stale', 0)
      }
    }, 60 * 1000)

    // Lightweight polling fallback: if realtime is stale for 3+ minutes and we haven't
    // refetched recently, do a single refetch. Check every 2 minutes.
    // This is a safety net for when realtime silently dies.
    pollTimer = setInterval(() => {
      if (typeof document !== 'undefined' && document.hidden) return
      const now = Date.now()
      const staleMs = now - lastRealtimeEventAt
      // Only poll if realtime is stale AND we haven't refetched in the last 30 seconds
      if (staleMs > 3 * 60 * 1000 && now - globalRefetchState.lastAutoRefetchAt > 30000) {
        globalRefetchState.lastAutoRefetchAt = now
        refetchOrdersNow()
      }
    }, 2 * 60 * 1000)

    return () => {
      isUnmounted = true
      // Clear any pending invalidate timer on cleanup
      if (invalidateTimer) {
        clearTimeout(invalidateTimer)
      }
      if (refetchTimer) {
        clearTimeout(refetchTimer)
      }
      clearReconnectTimer()
      if (watchdogTimer) clearInterval(watchdogTimer)
      if (pollTimer) clearInterval(pollTimer)
      window.removeEventListener('focus', handleResume)
      window.removeEventListener('pageshow', handleResume)
      window.removeEventListener('online', handleResume)
      document.removeEventListener('visibilitychange', handleResume)
      removeCurrentChannel()
    }
  }, [restaurantId, queryClient, isAuthReady])

  return query
}

/**
 * Fetch orders by table
 */
export function useTableOrders(tableId: string | null) {
  return useQuery({
    queryKey: queryKeys.orders.byTable(tableId || ''),
    staleTime: 2 * 60 * 1000, // 2 minutes - orders have realtime updates
    gcTime: 10 * 60 * 1000, // 10 minutes cache
    refetchOnWindowFocus: false, // Realtime handles updates
    refetchOnMount: false,
    queryFn: async (): Promise<Order[]> => {
      if (!tableId) {
        return []
      }

      const { data, error } = await supabase
        .from('orders')
        .select(`
          *,
          order_items (
            *,
            menu_item:menu_items (*)
          )
        `)
        .eq('table_id', tableId)
        .order('created_at', { ascending: false })

      if (error) {
        throw error
      }

      return (data || []).map((o) => {
        const order: Order = {
          id: o.id,
          tableId: o.table_id,
          serverId: o.server_id || o.staff_id || '',
          status: o.status as OrderStatus,
          readyAt: o.ready_at || null,
          completedAt: o.completed_at || null,
          createdAt: o.created_at || '',
          updatedAt: o.updated_at || '',
          items: (o.order_items || []).map((item: any) => {
            const orderItem: any = {
              id: item.id,
              orderId: item.order_id,
              menuItemId: item.menu_item_id,
              quantity: item.quantity,
              priceAtOrder: item.price_at_order,
              notes: item.notes || null,
              createdAt: item.created_at || '',
              updatedAt: item.updated_at || '',
            }
            if (item.menu_item) {
              orderItem.menuItem = {
                id: item.menu_item.id,
                restaurantId: item.menu_item.restaurant_id,
                nameAr: item.menu_item.name_ar,
                nameFr: item.menu_item.name_fr,
                descriptionAr: item.menu_item.description_ar || null,
                descriptionFr: item.menu_item.description_fr || null,
                price: item.menu_item.price,
                imageUrl: item.menu_item.image_url || null,
                imagePath: item.menu_item.image_path || null,
                isAvailable: item.menu_item.is_available ?? true,
                createdAt: item.menu_item.created_at || '',
                updatedAt: item.menu_item.updated_at || '',
              }
            }
            return orderItem
          }),
        }
        return order
      })
    },
    enabled: !!tableId,
  })
}

/**
 * Create new order mutation
 */
export function useCreateOrder(restaurantId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    retry: false, // Disable automatic retries
    mutationFn: async (request: {
      tableId: string
      staffId: string
      items: Array<{
        menuItemId: string
        quantity: number
        price: number
        notes?: string
        menuItemOptionId?: string
      }>
    }) => {
      // Step 1: Get or create active sub-order using proper parent/sub-order structure
      const { data: subOrderId, error: subOrderError } = await supabase.rpc('get_or_create_active_order', {
        p_restaurant_id: restaurantId,
        p_table_id: request.tableId,
        p_staff_id: request.staffId,
      })

      if (subOrderError) {
        throw subOrderError
      }

      if (!subOrderId) {
        throw new Error('Failed to get or create sub-order')
      }

      // Step 2: Add items to the sub-order
      const { data: addItemsResult, error: addItemsError } = await supabase.rpc('add_items_to_order', {
        p_order_id: subOrderId,
        p_items: request.items,
      })

      if (addItemsError) {
        throw addItemsError
      }

      // Check if the operation was successful
      if (addItemsResult && addItemsResult.length > 0) {
        const result = addItemsResult[0]
        if (!result?.success) {
          throw new Error(result?.message || 'Failed to add items to order')
        }
      }

      return { subOrderId, parentOrderId: addItemsResult?.[0]?.parent_order_id }
    },
    onMutate: async (variables) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: queryKeys.orders.list({ restaurantId }) })
      await queryClient.cancelQueries({ queryKey: queryKeys.tables.list(restaurantId) })

      // Get cached data
      const previousOrders = queryClient.getQueryData<Order[]>(queryKeys.orders.list({ restaurantId }))
      const previousTables = queryClient.getQueryData<any[]>(queryKeys.tables.list(restaurantId))
      const menuItems = queryClient.getQueryData<any[]>(queryKeys.menuItems.list({ restaurantId }))

      // Create optimistic order items
      const now = new Date().toISOString()
      const optimisticItems = variables.items.map((item, index) => {
        const menuItem = menuItems?.find(m => m.id === item.menuItemId)
        return {
          id: `temp-item-${Date.now()}-${index}`,
          orderId: 'temp-order',
          menuItemId: item.menuItemId,
          menuItemOptionId: item.menuItemOptionId || null,
          quantity: item.quantity,
          priceAtOrder: item.price,
          notes: item.notes || null,
          createdAt: now,
          updatedAt: now,
          menuItem: menuItem ? {
            id: menuItem.id,
            restaurantId: menuItem.restaurantId,
            nameAr: menuItem.nameAr,
            nameFr: menuItem.nameFr,
            descriptionAr: menuItem.descriptionAr,
            descriptionFr: menuItem.descriptionFr,
            price: menuItem.price,
            categoryId: menuItem.categoryId,
            imageUrl: menuItem.imageUrl,
            isAvailable: menuItem.isAvailable,
            createdAt: menuItem.createdAt,
            updatedAt: menuItem.updatedAt,
          } : undefined,
          portions: [], // Portions will be filled by realtime
          specialtyIds: [],
        }
      })

      // Optimistically update orders cache
      queryClient.setQueryData<Order[]>(
        queryKeys.orders.list({ restaurantId }),
        (old:any) => {
          if (!old) return old

          // Check if parent order exists for this table
          const existingParentOrder = old.find(
            (o:any) => o.tableId === variables.tableId && !o.parentOrderId && o.status !== 'completed'
          )

          if (existingParentOrder) {
            // Add to existing parent order as new sub-order
            return old.map((order:any) =>
              order.id === existingParentOrder.id
                ? {
                    ...order,
                    subOrders: [
                      ...(order.subOrders || []),
                      {
                        id: `temp-suborder-${Date.now()}`,
                        restaurantId,
                        tableId: variables.tableId,
                        serverId: variables.staffId,
                        status: 'pending' as OrderStatus,
                        parentOrderId: existingParentOrder.id,
                        createdAt: now,
                        updatedAt: now,
                        readyAt: null,
                        completedAt: null,
                        cancellationRequestedBy: null,
                        cancellationConfirmedBy: null,
                        cancellationRequestedAt: null,
                        items: optimisticItems,
                      },
                    ],
                  }
                : order
            )
          } else {
            // Create new parent order with sub-order
            const newParentOrder: any = {
              id: `temp-parent-${Date.now()}`,
              restaurantId,
              tableId: variables.tableId,
              serverId: variables.staffId,
              status: 'pending' as OrderStatus,
              parentOrderId: null,
              createdAt: now,
              updatedAt: now,
              readyAt: null,
              completedAt: null,
              cancellationRequestedBy: null,
              cancellationConfirmedBy: null,
              cancellationRequestedAt: null,
              items: [],
              subOrders: [
                {
                  id: `temp-suborder-${Date.now()}`,
                  restaurantId,
                  tableId: variables.tableId,
                  serverId: variables.staffId,
                  status: 'pending' as OrderStatus,
                  parentOrderId: `temp-parent-${Date.now()}`,
                  createdAt: now,
                  updatedAt: now,
                  readyAt: null,
                  completedAt: null,
                  cancellationRequestedBy: null,
                  cancellationConfirmedBy: null,
                  cancellationRequestedAt: null,
                  items: optimisticItems 
                },
              ],
            }
            return [...old, newParentOrder]
          }
        }
      )

      // Optimistically update tables cache to mark table as occupied
      queryClient.setQueryData<any[]>(
        queryKeys.tables.list(restaurantId),
        (old) => {
          if (!old) return old
          return old.map(table =>
            table.id === variables.tableId
              ? { ...table, status: 'occupied', updatedAt: now }
              : table
          )
        }
      )

      // Record manual update to prevent duplicate realtime invalidation
      lastManualRefetchByRestaurant.set(restaurantId, Date.now())

      return { previousOrders, previousTables }
    },
    onError: async (err:any, variables:any, context:any) => {
      // Check if this is an authorization error (deleted/inactive staff)
      await handleAuthorizationError(err)

      // Rollback on error
      if (context?.previousOrders) {
        queryClient.setQueryData(queryKeys.orders.list({ restaurantId }), context.previousOrders)
      }
      if (context?.previousTables) {
        queryClient.setQueryData(queryKeys.tables.list(restaurantId), context.previousTables)
      }
    },
    onSuccess: (data, variables) => {
      // Replace temp IDs with real IDs from server response
      if (data?.subOrderId && data?.parentOrderId) {
        const previousOrders = queryClient.getQueryData<Order[]>(queryKeys.orders.list({ restaurantId }))

        // Check if we're adding to an existing pending order
        const existingParentOrder = previousOrders?.find(
          (o) => o.tableId === variables.tableId && !o.parentOrderId &&
          (o.status === 'pending' || o.status === 'in_progress')
        )
        const isAddingToExistingOrder = existingParentOrder &&
          existingParentOrder.subOrders?.some((so) => so.id === data.subOrderId && !so.id.startsWith('temp-'))

        queryClient.setQueryData<Order[]>(
          queryKeys.orders.list({ restaurantId }),
          (old:any) => {
            if (!old) return old

            return old.map((parentOrder:any) => {
              // Update parent order ID if it's a temp ID
              if (parentOrder.id.startsWith('temp-parent-')) {
                return {
                  ...parentOrder,
                  id: data.parentOrderId,
                  subOrders: parentOrder.subOrders?.map((subOrder:any) =>
                    subOrder.id.startsWith('temp-suborder-')
                      ? { ...subOrder, id: data.subOrderId, parentOrderId: data.parentOrderId }
                      : subOrder
                  ),
                }
              }

              // Update sub-order ID in existing parent
              if (parentOrder.subOrders?.some((so:any) => so.id.startsWith('temp-suborder-'))) {
                return {
                  ...parentOrder,
                  subOrders: parentOrder.subOrders.map((subOrder:any) =>
                    subOrder.id.startsWith('temp-suborder-')
                      ? { ...subOrder, id: data.subOrderId }
                      : subOrder
                  ),
                }
              }

              return parentOrder
            })
          }
        )

        // Dispatch order-updated event if adding to existing pending order
        if (isAddingToExistingOrder) {
          const event = new CustomEvent('order-updated', {
            detail: {
              orderId: data.subOrderId,
              tableId: variables.tableId,
              isSubOrder: true,
            },
          })
          window.dispatchEvent(event)
        }
      }

      // Note: We don't do an explicit refetch here. The optimistic update handles the UI,
      // and the realtime subscription will sync complete data (portions, specialtyIds) shortly.
      // This reduces API calls while maintaining data freshness.

      // Invalidate menu item availability cache (will refetch when component needs it)
      queryClient.invalidateQueries({
        queryKey: ['menuItemAvailability', restaurantId],
        refetchType: 'none', // Mark stale, don't refetch immediately
      })
      queryClient.invalidateQueries({
        queryKey: ['menuItemOptionAvailability'],
        refetchType: 'none',
      })
    },
  })
}

/**
 * Update order status mutation
 */
export function useUpdateOrderStatus(restaurantId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (request: {
      orderId: string
      status: OrderStatus
    }) => {
      // Use RPC function to update order status (bypasses RLS for staff)
      const { data, error } = await supabase.rpc('update_order_status_for_staff', {
        p_order_id: request.orderId,
        p_status: request.status,
      })

      if (error) {
        throw error
      }

      if (data && data.length > 0) {
        const firstResult = data[0]
        if (firstResult && !firstResult.success) {
          throw new Error(firstResult.message || 'Operation failed')
        }
      }

      return data
    },
    onMutate: async (variables) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: queryKeys.orders.list({ restaurantId }) })

      // Snapshot the previous value
      const previousOrders = queryClient.getQueryData<Order[]>(queryKeys.orders.list({ restaurantId }))

      // Optimistically update the cache
      queryClient.setQueryData<Order[]>(
        queryKeys.orders.list({ restaurantId }),
        (old) => {
          if (!old) return old

          return old.map(parentOrder => {
            // Check if this is the order to update
            if (parentOrder.id === variables.orderId) {
              return {
                ...parentOrder,
                status: variables.status,
                readyAt: variables.status === 'ready' ? new Date().toISOString() : parentOrder.readyAt,
                updatedAt: new Date().toISOString(),
              }
            }

            // Check sub-orders
            if (parentOrder.subOrders) {
              const hasMatchingSubOrder = parentOrder.subOrders.some(so => so.id === variables.orderId)
              if (hasMatchingSubOrder) {
                return {
                  ...parentOrder,
                  subOrders: parentOrder.subOrders.map(subOrder =>
                    subOrder.id === variables.orderId
                      ? {
                          ...subOrder,
                          status: variables.status,
                          readyAt: variables.status === 'ready' ? new Date().toISOString() : subOrder.readyAt,
                          updatedAt: new Date().toISOString(),
                        }
                      : subOrder
                  ),
                }
              }
            }

            return parentOrder
          })
        }
      )

      // Record manual update to prevent duplicate realtime invalidation
      lastManualRefetchByRestaurant.set(restaurantId, Date.now())

      // Return context for rollback
      return { previousOrders }
    },
    onError: async (err, variables, context) => {
      // Check if this is an authorization error (deleted/inactive staff)
      await handleAuthorizationError(err)

      // Rollback to previous state on error
      if (context?.previousOrders) {
        queryClient.setQueryData(queryKeys.orders.list({ restaurantId }), context.previousOrders)
      }
    },
    onSettled: (data, error, variables) => {
      // No refetch needed - realtime subscription will sync changes from other users
      
      // If order was marked as ready, inventory was deducted, so invalidate availability cache
      if (variables.status === 'ready') {
        queryClient.invalidateQueries({
          queryKey: ['menuItemAvailability', restaurantId],
          refetchType: 'none', // Mark stale, don't refetch immediately
        })
        queryClient.invalidateQueries({
          queryKey: ['menuItemOptionAvailability'],
          refetchType: 'none',
        })
      }
    },
  })
}

/**
 * Complete parent order mutation - marks parent order as completed
 */
export function useCompleteParentOrder(restaurantId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (request: {
      parentOrderId: string
    }) => {
      // Use RPC function to complete parent order
      const { data, error } = await (supabase.rpc as any)('complete_parent_order', {
        p_parent_order_id: request.parentOrderId,
      })

      if (error) {
        throw error
      }

      if (data && data.length > 0) {
        const firstResult = data[0]
        if (firstResult && !firstResult.success) {
          throw new Error(firstResult.message || 'Operation failed')
        }
      }

      return data
    },
    onMutate: async (variables) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.orders.list({ restaurantId }) })

      const previousOrders = queryClient.getQueryData<Order[]>(queryKeys.orders.list({ restaurantId }))

      // Optimistically mark parent order as completed
      queryClient.setQueryData<Order[]>(
        queryKeys.orders.list({ restaurantId }),
        (old) => {
          if (!old) return old

          return old.map(order =>
            order.id === variables.parentOrderId
              ? {
                  ...order,
                  status: 'completed' as OrderStatus,
                  completedAt: new Date().toISOString(),
                  updatedAt: new Date().toISOString(),
                }
              : order
          )
        }
      )

      lastManualRefetchByRestaurant.set(restaurantId, Date.now())

      return { previousOrders }
    },
    onError: async (err, variables, context) => {
      // Check if this is an authorization error (deleted/inactive staff)
      await handleAuthorizationError(err)

      if (context?.previousOrders) {
        queryClient.setQueryData(queryKeys.orders.list({ restaurantId }), context.previousOrders)
      }
    },
    onSettled: () => {
      // No refetch - realtime will sync
    },
  })
}

/**
 * Request order cancellation mutation - first step, requires confirmation
 */
export function useRequestOrderCancellation(restaurantId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (request: {
      orderId: string
      staffId: string
    }) => {
      const { data, error } = await (supabase.rpc as any)('request_order_cancellation', {
        p_order_id: request.orderId,
        p_staff_id: request.staffId,
      })

      if (error) {
        throw error
      }

      if (data && data.length > 0) {
        const firstResult = data[0]
        if (firstResult && !firstResult.success) {
          throw new Error(firstResult.message || 'Operation failed')
        }
      }

      return data
    },
    onSuccess: async () => {
      // Record manual refetch to prevent duplicate realtime invalidation
      lastManualRefetchByRestaurant.set(restaurantId, Date.now())

      // Refetch immediately for the browser that made the change (instant UI feedback)
      // Other browsers will be notified via debounced realtime subscription
      await queryClient.refetchQueries({
        queryKey: queryKeys.orders.list({ restaurantId }),
        type: 'active',
      })
    },
  })
}

/**
 * Confirm order cancellation mutation - admin or assistant with cancellations.accept_reject permission
 * Supports both admin users and assistants with appropriate permissions.
 * For assistants, the backend RPC verifies permissions - this protects both frontend and server side.
 */
export function useConfirmOrderCancellation(restaurantId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (request: {
      orderId: string
      staffId: string
    }) => {
      // Check if assistant is logged in
      if (isAssistantLoggedIn()) {

        // Use assistant RPC function which validates permissions server-side
        const { data, error } = await (supabase.rpc as any)('assistant_confirm_order_cancellation', {
          p_restaurant_id: restaurantId,
          p_order_id: request.orderId,
        })

        if (error) {
          throw error
        }

        if (data && data.length > 0) {
          const firstResult = data[0]
          if (firstResult && !firstResult.success) {
            // Check for permission denied
            if (firstResult.message?.includes('PERMISSION_DENIED')) {
              throw new Error('Permission denied')
            }
            throw new Error(firstResult.message || 'Operation failed')
          }
        }

        return data
      }

      // Admin user - use original RPC function
      const { data, error } = await (supabase.rpc as any)('confirm_order_cancellation', {
        p_order_id: request.orderId,
      })

      if (error) {
        throw error
      }

      if (data && data.length > 0) {
        const firstResult = data[0]
        if (firstResult && !firstResult.success) {
          throw new Error(firstResult.message || 'Operation failed')
        }
      }

      return data
    },
    onSuccess: async () => {
      // Record manual refetch to prevent duplicate realtime invalidation
      lastManualRefetchByRestaurant.set(restaurantId, Date.now())

      // Refetch immediately for the browser that made the change (instant UI feedback)
      // Other browsers will be notified via debounced realtime subscription
      await Promise.all([
        queryClient.refetchQueries({
          queryKey: queryKeys.orders.list({ restaurantId }),
          type: 'active',
        }),
        queryClient.refetchQueries({
          queryKey: queryKeys.tables.list(restaurantId),
          type: 'active',
        }),
      ])
    },
  })
}

/**
 * Reject order cancellation mutation - admin or assistant with cancellations.accept_reject permission
 * Supports both admin users and assistants with appropriate permissions.
 * For assistants, the backend RPC verifies permissions - this protects both frontend and server side.
 */
export function useRejectOrderCancellation(restaurantId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (request: {
      orderId: string
      staffId: string
    }) => {
      // Check if assistant is logged in
      if (isAssistantLoggedIn()) {

        // Use assistant RPC function which validates permissions server-side
        const { data, error } = await (supabase.rpc as any)('assistant_reject_order_cancellation', {
          p_restaurant_id: restaurantId,
          p_order_id: request.orderId,
        })

        if (error) {
          throw error
        }

        if (data && data.length > 0) {
          const firstResult = data[0]
          if (firstResult && !firstResult.success) {
            // Check for permission denied
            if (firstResult.message?.includes('PERMISSION_DENIED')) {
              throw new Error('Permission denied')
            }
            throw new Error(firstResult.message || 'Operation failed')
          }
        }

        return data
      }

      // Admin user - use original RPC function
      const { data, error } = await (supabase.rpc as any)('reject_order_cancellation', {
        p_order_id: request.orderId,
      })

      if (error) {
        throw error
      }

      if (data && data.length > 0) {
        const firstResult = data[0]
        if (firstResult && !firstResult.success) {
          throw new Error(firstResult.message || 'Operation failed')
        }
      }

      return data
    },
    onSuccess: async () => {
      // Record manual refetch to prevent duplicate realtime invalidation
      lastManualRefetchByRestaurant.set(restaurantId, Date.now())

      // Refetch immediately for the browser that made the change (instant UI feedback)
      // Other browsers will be notified via debounced realtime subscription
      await queryClient.refetchQueries({
        queryKey: queryKeys.orders.list({ restaurantId }),
        type: 'active',
      })
    },
  })
}

/**
 * Cancel parent order when all sub-orders are cancelled
 */
export function useCancelParentOrderWhenAllSubOrdersCancelled(restaurantId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (request: {
      parentOrderId: string
      staffId: string
    }) => {
      const { data, error } = await (supabase.rpc as any)('cancel_parent_order_when_all_sub_orders_cancelled', {
        p_parent_order_id: request.parentOrderId,
        p_staff_id: request.staffId,
      })

      if (error) {
        throw error
      }

      if (data && data.length > 0) {
        const firstResult = data[0]
        if (firstResult && !firstResult.success) {
          throw new Error(firstResult.message || 'Operation failed')
        }
      }

      return data
    },
    onSuccess: async () => {
      // Record manual refetch to prevent duplicate realtime invalidation
      lastManualRefetchByRestaurant.set(restaurantId, Date.now())

      // Refetch immediately for the browser that made the change (instant UI feedback)
      // Other browsers will be notified via debounced realtime subscription
      await Promise.all([
        queryClient.refetchQueries({
          queryKey: queryKeys.orders.list({ restaurantId }),
          type: 'active',
        }),
        queryClient.refetchQueries({
          queryKey: queryKeys.tables.list(restaurantId),
          type: 'active',
        }),
      ])
    },
  })
}

/**
 * Update order item portion status mutation - for kitchen staff to update their portion
 */
export function useUpdatePortionStatus(restaurantId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (request: {
      portionId: string
      status: 'pending' | 'in_progress' | 'ready'
      staffId: string
    }) => {
      const { data, error } = await (supabase.rpc as any)('update_order_item_portion_status', {
        p_portion_id: request.portionId,
        p_status: request.status,
        p_staff_id: request.staffId,
      })

      if (error) {
        throw error
      }

      return data
    },
    onMutate: async (variables) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: queryKeys.orders.list({ restaurantId }) })

      // Snapshot the previous value
      const previousOrders = queryClient.getQueryData<Order[]>(queryKeys.orders.list({ restaurantId }))

      // Optimistically update the portion status in cache
      queryClient.setQueryData<Order[]>(
        queryKeys.orders.list({ restaurantId }),
        (old) => {
          if (!old) return old

          return old.map(parentOrder => {
            if (!parentOrder.subOrders) return parentOrder

            const updatedSubOrders = parentOrder.subOrders.map(subOrder => {
              if (!subOrder.items) return subOrder

              // Check if this subOrder contains the portion being updated
              const hasPortionBeingUpdated = subOrder.items.some(item =>
                item.portions?.some(p => p.id === variables.portionId)
              )

              // Only update items and check readiness if this subOrder contains the portion
              if (!hasPortionBeingUpdated) {
                return subOrder
              }

              const updatedItems = subOrder.items.map(item => {
                if (!item.portions) return item

                const updatedPortions = item.portions.map(portion =>
                  portion.id === variables.portionId
                    ? { ...portion, status: variables.status, updatedAt: new Date().toISOString() }
                    : portion
                )

                return { ...item, portions: updatedPortions }
              })

              // Calculate status based on ALL portions in the suborder
              const allPortions = updatedItems.flatMap(item => item.portions || [])

              let newStatus: OrderStatus = subOrder.status
              let readyAt = subOrder.readyAt

              if (allPortions.length > 0) {
                // Check portion statuses
                const hasPending = allPortions.some(p => p.status === 'pending')
                const hasInProgress = allPortions.some(p => p.status === 'in_progress')
                const allReady = allPortions.every(p => p.status === 'ready')

                // Status logic: pending > in_progress > ready
                if (allReady) {
                  newStatus = 'ready'
                  readyAt = new Date().toISOString()
                } else if (hasPending) {
                  newStatus = 'pending'
                  readyAt = null
                } else if (hasInProgress) {
                  newStatus = 'in_progress'
                  readyAt = null
                }
              }

              return {
                ...subOrder,
                items: updatedItems,
                status: newStatus,
                readyAt: readyAt,
                updatedAt: new Date().toISOString(),
              }
            })

            return { ...parentOrder, subOrders: updatedSubOrders }
          })
        }
      )

      // Record manual update to prevent duplicate realtime invalidation
      lastManualRefetchByRestaurant.set(restaurantId, Date.now())

      // Dispatch portion-status-changed event if status is ready (as backup to realtime)
      // Find the portion details from previousOrders (before update) to get correct oldStatus
      if (variables.status === 'ready' && previousOrders) {
        for (const parentOrder of previousOrders) {
          if (parentOrder.subOrders) {
            for (const subOrder of parentOrder.subOrders) {
              if (subOrder.items) {
                for (const item of subOrder.items) {
                  if (item.portions) {
                    const portion = item.portions.find(p => p.id === variables.portionId)
                    if (portion && portion.status !== 'ready') {
                      // Dispatch event for immediate notification (realtime will also fire, but this ensures it works)
                      window.dispatchEvent(new CustomEvent('portion-status-changed', {
                        detail: {
                          portionId: variables.portionId,
                          orderItemId: item.id,
                          specialtyId: portion.specialtyId,
                          oldStatus: portion.status,
                          newStatus: 'ready',
                        }
                      }))
                      return { previousOrders }
                    }
                  }
                }
              }
            }
          }
        }
      }

      return { previousOrders }
    },
    onError: async (err, variables, context) => {
      // Check if this is an authorization error (deleted/inactive staff)
      await handleAuthorizationError(err)

      // Rollback on error
      if (context?.previousOrders) {
        queryClient.setQueryData(queryKeys.orders.list({ restaurantId }), context.previousOrders)
      }
    },
    onSettled: () => {
      // No refetch needed - realtime subscription will sync changes from other users
    },
  })
}

/**
 * Check table mutation - marks all ready orders as checked and table as available
 */
export function useCheckTable(restaurantId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (request: {
      tableId: string
    }) => {
      // Use RPC function to check table (bypasses RLS for staff)
      const { data, error } = await (supabase.rpc as any)('check_table', {
        p_table_id: request.tableId,
        p_restaurant_id: restaurantId,
      })

      if (error) {
        throw error
      }

      if (data && data.length > 0) {
        const firstResult = data[0]
        if (firstResult && !firstResult.success) {
          throw new Error(firstResult.message || 'Operation failed')
        }
      }

      return data
    },
    onMutate: async (variables) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.orders.list({ restaurantId }) })
      await queryClient.cancelQueries({ queryKey: queryKeys.tables.list(restaurantId) })

      const previousOrders = queryClient.getQueryData<Order[]>(queryKeys.orders.list({ restaurantId }))
      const previousTables = queryClient.getQueryData<any[]>(queryKeys.tables.list(restaurantId))

      const now = new Date().toISOString()

      // Optimistically mark all ready orders for this table as "checked"
      queryClient.setQueryData<Order[]>(
        queryKeys.orders.list({ restaurantId }),
        (old:any) => {
          if (!old) return old

          return old.map((order:any) => {
            if (order.tableId !== variables.tableId) return order

            // Mark parent orders and sub-orders as checked if they're ready
            if (order.status === 'ready') {
              return {
                ...order,
                status: 'checked' as any,
                updatedAt: now,
                subOrders: order.subOrders?.map((so:any) =>
                  so.status === 'ready'
                    ? { ...so, status: 'checked' as any, updatedAt: now }
                    : so
                ),
              }
            }

            // Also update sub-orders if parent is not ready but some sub-orders are
            if (order.subOrders) {
              return {
                ...order,
                subOrders: order.subOrders.map((so:any) =>
                  so.status === 'ready'
                    ? { ...so, status: 'checked' as any, updatedAt: now }
                    : so
                ),
              }
            }

            return order
          })
        }
      )

      // Optimistically mark table as available
      queryClient.setQueryData<any[]>(
        queryKeys.tables.list(restaurantId),
        (old) => {
          if (!old) return old
          return old.map(table =>
            table.id === variables.tableId
              ? { ...table, status: 'available', updatedAt: now }
              : table
          )
        }
      )

      lastManualRefetchByRestaurant.set(restaurantId, Date.now())

      return { previousOrders, previousTables }
    },
    onError: async (err, variables, context) => {
      // Check if this is an authorization error (deleted/inactive staff)
      await handleAuthorizationError(err)

      if (context?.previousOrders) {
        queryClient.setQueryData(queryKeys.orders.list({ restaurantId }), context.previousOrders)
      }
      if (context?.previousTables) {
        queryClient.setQueryData(queryKeys.tables.list(restaurantId), context.previousTables)
      }
    },
    onSettled: () => {
      // No refetch - realtime will sync
    },
  })
}

/**
 * Delete order item mutation - only for pending orders
 */
export function useDeleteOrderItem(restaurantId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (request: {
      orderItemId: string
      staffId: string
      orderId?: string
      tableId?: string
    }) => {
      const { data, error } = await (supabase.rpc as any)('delete_order_item', {
        p_order_item_id: request.orderItemId,
        p_staff_id: request.staffId,
      })

      if (error) {
        throw error
      }

      if (data && data.length > 0) {
        const firstResult = data[0]
        if (firstResult && !firstResult.success) {
          throw new Error(firstResult.message || 'Operation failed')
        }
      }

      return data
    },
    onSuccess: async (data, variables) => {
      // Record manual refetch to prevent duplicate realtime invalidation
      lastManualRefetchByRestaurant.set(restaurantId, Date.now())

      // Refetch to get updated order data
      await queryClient.refetchQueries({
        queryKey: queryKeys.orders.list({ restaurantId }),
        type: 'active',
      })

      // Dispatch order-updated event if orderId and tableId are provided
      if (variables.orderId && variables.tableId) {
        const event = new CustomEvent('order-updated', {
          detail: {
            orderId: variables.orderId,
            tableId: variables.tableId,
            isSubOrder: true,
          },
        })
        window.dispatchEvent(event)
      }
    },
  })
}

